import { useState, useEffect } from "react";
import { DateTime } from "luxon";
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
  SidePanelFooter,
} from "@/components/ui/SidePanel";
import { Button } from "@/components/ui/Button";
import { useUpdateUserProfileMutation } from "@/api/clubsApi";
import type { UserProfile } from "@/api/clubsApi";
import { cn } from "@/components/ui/cn";

interface EditBasicInfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: UserProfile;
}

const inputClassName = cn(
  "w-full rounded-2xl border border-border-tertiary p-3",
  "text-base text-text-primary bg-input-background",
  "placeholder:text-text-tertiary",
  "focus:outline-none focus:ring-2 focus:ring-text-primary focus:ring-offset-2 focus:ring-offset-background",
  "focus:border-text-primary"
);

export function EditBasicInfoModal({
  open,
  onOpenChange,
  profile,
}: EditBasicInfoModalProps) {
  const [updateProfile, { isLoading: isSaving }] = useUpdateUserProfileMutation();

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [pronouns, setPronouns] = useState("");

  // Initialize form state when modal opens
  useEffect(() => {
    if (open && profile) {
      setFirstName(profile.first_name || "");
      setLastName(profile.last_name || "");
      setUsername(profile.username || "");
      setPronouns(profile.pronouns || "");
    }
  }, [open, profile]);

  const handleSave = async () => {
    try {
      await updateProfile({
        id: profile.id,
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        username: username.trim() || null,
        pronouns: pronouns.trim() || null,
        updated_at: DateTime.now().toISO(),
      }).unwrap();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update profile:", error);
    }
  };

  return (
    <SidePanel open={open} onOpenChange={onOpenChange}>
      <SidePanelContent>
        <SidePanelHeader title="Edit basic info" onClose={() => onOpenChange(false)} />

        <SidePanelBody className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-body-3 text-text-secondary">First name</label>
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                className={inputClassName}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-body-3 text-text-secondary">Last name</label>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
                className={inputClassName}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-body-3 text-text-secondary">Username</label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="username"
              className={inputClassName}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-body-3 text-text-secondary">Pronouns</label>
            <input
              value={pronouns}
              onChange={(e) => setPronouns(e.target.value)}
              placeholder="e.g. she/her, he/him, they/them"
              className={inputClassName}
            />
          </div>
        </SidePanelBody>

        <SidePanelFooter className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-text-primary text-text-reversed hover:bg-text-primary/90"
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </SidePanelFooter>
      </SidePanelContent>
    </SidePanel>
  );
}
